import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { ChatbotApi, type IChatbotField } from 'entities/Chatbot'
import type {
  IChatMessage,
  IParamsSendSessionMessage,
  IWidget,
} from 'entities/Chatbot/api/chatbotTypes'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type UnifiedAgentStore } from 'pages/chatbot/ui/UnifiedAgent/store/unifiedAgentStore'

export type IChatMessageUI = IChatMessage & { hasError?: boolean }

type ILocalWidgetState = {
  settings?: Partial<IParamsSendSessionMessage['current_settings']>
  booking_fields?: IChatbotField[]
}

export class ChatStore {
  sessionId: string | null = null
  messages: IChatMessageUI[] = []
  isLoading = false
  widget: IWidget | null = null
  inputValue = ''
  private _pendingMessage: string | null = null
  private _pendingCallback: (() => void) | null = null
  isLoadingHistory = false
  private _localWidgetState: ILocalWidgetState = {}

  constructor(private readonly agent: UnifiedAgentStore, readonly entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get hasMessages() {
    return this.messages.length > 0
  }

  private get _sessionParams(): Omit<IParamsSendSessionMessage, 'message'> {
    return {
      booking_fields: this.agent.bookingSectionStore.bookingFields,
      current_settings: this.agent.commonEnhancePayload.settings,
      used_fields: this.agent.instructionsSectionStore.existMergeFieldsPayload,
    }
  }

  get allMergeFields() {
    return this.entities.mergeFieldStore.allFields
  }

  setLocalWidgetState = (state: ILocalWidgetState) => {
    this._localWidgetState = state
  }

  setInputValue = (value: string) => {
    this.inputValue = value
  }

  initWithPrompt = async (prompt: string) => {
    this._pendingMessage = prompt
    this.messages = [{ role: 'user', content: prompt }]
    this.isLoading = true

    try {
      const { data: sessionData } = await ChatbotApi.createChatSession()

      runInAction(() => {
        this.sessionId = sessionData.session_id
      })

      await this._sendToSession(sessionData.session_id, prompt)
    } catch (error) {
      logger.error(error)
      runInAction(() => {
        this.isLoading = false
        this._setLastUserMessageError(true)
      })
    }
  }

  sendMessage = async (message = this.inputValue.trim(), onSuccess?: () => void) => {
    if (!message || this.isLoading) return

    this._pendingCallback = onSuccess ?? null

    if (!this.sessionId) {
      this.inputValue = ''
      return this.initWithPrompt(message)
    }

    this._pendingMessage = message
    this.messages.push({ role: 'user', content: message })
    this.inputValue = ''
    this.widget = null
    this.isLoading = true

    try {
      await this._sendToSession(this.sessionId, message)
    } catch (error) {
      logger.error(error)
      runInAction(() => {
        this.isLoading = false
        this._setLastUserMessageError(true)
      })
    }
  }

  retry = async () => {
    if (!this._pendingMessage || this.isLoading) return

    const message = this._pendingMessage
    this._setLastUserMessageError(false)
    this.isLoading = true

    try {
      if (!this.sessionId) {
        const { data: sessionData } = await ChatbotApi.createChatSession()
        runInAction(() => {
          this.sessionId = sessionData.session_id
        })
        await this._sendToSession(sessionData.session_id, message)
      } else {
        await this._sendToSession(this.sessionId, message)
      }
    } catch (error) {
      logger.error(error)
      runInAction(() => {
        this.isLoading = false
        this._setLastUserMessageError(true)
      })
    }
  }

  loadHistory = async (sessionId: string) => {
    this.isLoadingHistory = true
    try {
      const { data } = await ChatbotApi.getSessionMessage(sessionId)
      runInAction(() => {
        this.sessionId = sessionId
        this.messages = data.messages
        this.widget = data.messages.at(-1)?.widget ?? null
        this.isLoadingHistory = false
      })
    } catch (error) {
      logger.error(error)
      runInAction(() => {
        this.isLoadingHistory = false
      })
    }
  }

  private _setLastUserMessageError = (hasError: boolean) => {
    const idx = this.messages.findLastIndex((m) => m.role === 'user')
    if (idx !== -1) {
      this.messages[idx] = { ...this.messages[idx], hasError }
    }
  }

  private _sendToSession = async (sessionId: string, message: string) => {
    try {
      const { data } = await ChatbotApi.sendSessionMessage(sessionId, {
        message,
        ...this._sessionParams,
        current_settings: {
          ...this._sessionParams.current_settings,
          ...this._localWidgetState.settings,
        },
        ...(this._localWidgetState.booking_fields && {
          booking_fields: this._localWidgetState.booking_fields,
        }),
      })

      runInAction(() => {
        this.messages.push({
          role: 'assistant',
          content: data.message,
          widget: data.widget,
        })
        this.widget = data.widget ?? null

        if (data.settings_patch?.instructions) {
          this.agent.instructionsSectionStore.setInstructions(data.settings_patch.instructions)
        }

        this._localWidgetState = {}
        this._pendingCallback?.()
        this._pendingCallback = null
      })
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }
}
